import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js/min";
import metadata from "libphonenumber-js/min/metadata";
import { DEFAULT_PHONE_COUNTRY } from "./locale";

/**
 * Parses a phone number, assuming Australia when there's no +country code.
 * Returns E.164 (e.g. +61412345678) or null when invalid.
 */
export function normalisePhoneNumber(input: string): string | null {
  const parsed = parsePhoneNumberFromString(input.trim(), DEFAULT_PHONE_COUNTRY);
  if (!parsed || !parsed.isValid()) return null;
  return parsed.number;
}

const PHONE_CHARACTERS = /^\+?[\d\s\-().]+$/;

/**
 * What a lead's phone number is stored as. Numbers libphonenumber recognises
 * come back normalised; anything else that looks like a phone number (8–15
 * digits with the country code, as E.164 allows) is kept as typed: we'd rather take an unusual
 * number than turn a lead away. Numbers without a +country code are read as
 * Australian. Null when it isn't a phone number at all.
 */
export function toStoredPhoneNumber(input: string): string | null {
  const trimmed = input.trim();
  if (!PHONE_CHARACTERS.test(trimmed)) return null;
  const normalised = normalisePhoneNumber(trimmed);
  if (normalised) return normalised;
  const digits = trimmed.replace(/\D/g, "");
  const international = trimmed.startsWith("+")
    ? digits
    : `${getCountryCallingCode(DEFAULT_PHONE_COUNTRY)}${digits.replace(/^0/, "")}`;
  return /^[1-9]\d{7,14}$/.test(international) ? `+${international}` : null;
}

export interface PhoneCountry {
  /** ISO 3166-1 alpha-2, e.g. "AU". */
  code: CountryCode;
  /** Without the +, e.g. "61". */
  dialCode: string;
}

/** Every country libphonenumber knows, with its dialling code. */
export function phoneCountries(): PhoneCountry[] {
  return getCountries().map((code) => ({ code, dialCode: getCountryCallingCode(code) }));
}

/**
 * Splits E.164 into a country and the national number (digits only), for an
 * input that shows them separately. Shared codes such as +1 fall back to their
 * main country (US) when the number doesn't say which one it is. Null when
 * there's no known country code.
 */
export function splitPhoneNumber(
  e164: string,
): { country: CountryCode; nationalNumber: string } | null {
  const parsed = parsePhoneNumberFromString(e164);
  if (parsed) {
    const country =
      parsed.country ?? metadata.country_calling_codes[parsed.countryCallingCode]?.[0];
    if (country) return { country: country as CountryCode, nationalNumber: parsed.nationalNumber };
  }
  // Too short or unknown to parse: match the longest dialling code it starts with.
  const digits = e164.replace(/\D/g, "");
  for (let length = 3; length >= 1; length--) {
    const country = metadata.country_calling_codes[digits.slice(0, length)]?.[0];
    if (country) return { country: country as CountryCode, nationalNumber: digits.slice(length) };
  }
  return null;
}

/** Friendly display of an E.164 number (Australian numbers in national format). */
export function formatPhone(e164: string): string {
  const parsed = parsePhoneNumberFromString(e164);
  if (!parsed) return e164;
  return parsed.country === DEFAULT_PHONE_COUNTRY
    ? parsed.formatNational()
    : parsed.formatInternational();
}

const PHONE_QUERY_PATTERN = /^\+?[\d\s\-().]+$/;

/**
 * The digit strings a phone-like search query could match, so "0412 345 678",
 * "+61 412 345 678" and "412345678" all find a number stored as +61412345678.
 * Covers the digits-only form plus the Australian national (0…) and
 * international (+61…) spellings of the same digits. Empty when the query
 * isn't phone-like.
 */
export function phoneSearchVariants(query: string): string[] {
  const trimmed = query.trim();
  if (!PHONE_QUERY_PATTERN.test(trimmed)) return [];
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return [];
  const variants = [digits];
  if (digits.startsWith("0") && digits.length > 1) variants.push(`+61${digits.slice(1)}`);
  if (digits.startsWith("61") && digits.length > 2) variants.push(`0${digits.slice(2)}`);
  return [...new Set(variants)];
}
